import { createContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

const TableContext = createContext(null);

/**
 * TanStack `table` 實例每輪 render 可能換引用；以 Proxy + 精選 deps 穩定 Context，
 * 避免列表連點選列時子樹過度 re-render（SP-08）。
 */
/* eslint-disable react-hooks/refs -- proxy + ref sync is intentional for stable table context */
export function TableProvider({ children, value }) {
  const valueRef = useRef(value);
  const proxyRef = useRef(null);

  if (proxyRef.current == null) {
    proxyRef.current = new Proxy(
      {},
      {
        get(_target, prop) {
          return valueRef.current[prop];
        },
        has(_target, prop) {
          return prop in valueRef.current;
        },
      },
    );
  }

  valueRef.current = value;

  const contextValue = useMemo(
    () => ({
      proxy: proxyRef.current,
      rowSelection: value.rowSelection,
      isLoading: value.isLoading,
      sorting: value.sorting,
      globalFilter: value.globalFilter,
      columnFilters: value.columnFilters,
      columnSizing: value.columnSizing,
      columnOrder: value.columnOrder,
      enableColumnOrder: value.enableColumnOrder,
      currentPage: value.pagination?.current_page,
      pageSize: value.pagination?.page_size,
      totalCount: value.pagination?.total_count,
      totalPages: value.pagination?.total_page,
      data: value.table?.options?.data,
    }),
    [
      value.rowSelection,
      value.isLoading,
      value.sorting,
      value.globalFilter,
      value.columnFilters,
      value.columnSizing,
      value.columnOrder,
      value.enableColumnOrder,
      value.pagination?.current_page,
      value.pagination?.page_size,
      value.pagination?.total_count,
      value.pagination?.total_page,
      value.table?.options?.data,
    ],
  );

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
}
/* eslint-enable react-hooks/refs */

TableProvider.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.object.isRequired,
};

export default TableContext;
